import { useCallback, useEffect, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Sparkles, Layers, Check,
  AlertCircle, Lightbulb, ListChecks, Users, Rocket, SearchX,
  Fingerprint, Shield, ThumbsUp, ThumbsDown, CheckCircle2, AlertTriangle, Link2, Wand2,
  Pencil, Plus, X,
} from 'lucide-react';
import { api } from '../../lib/api';
import { CreditBanner, isCreditError } from './CreditBanner';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import {
  SectionCard, ProvenanceLabel, Skeleton, Disclaimer, InfoCard, FeatureChips, GenerationProgress,
  ModelCostBar,
} from './ui';
import { ScoreGauge } from './charts';
import { useVersions } from './useVersions';
import { generateBlocked } from './ideaGate';
import { parsePartialJson } from '../../lib/partialJson';
import { ServedModelBadge } from './ServedModelBadge';

function Bullets({ items, testId }) {
  if (!Array.isArray(items) || items.length === 0) return <span className="text-muted-foreground text-sm">—</span>;
  return <ul className="list-disc ps-5 space-y-1" data-testid={testId}>{items.map((x, i) => <li key={`${x}-${i}`}>{x}</li>)}</ul>;
}

function IndustryTile({ active, icon, label, onClick, testId, fallbackIcon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      aria-pressed={active}
      className={`group relative flex flex-col items-center justify-center gap-2 rounded-md border p-4 text-center transition-colors ${
        active
          ? 'border-primary ring-2 ring-primary bg-muted'
          : 'border-border hover:border-muted-foreground bg-card'
      }`}
    >
      {active && (
        <span className="absolute top-1.5 end-1.5 text-foreground">
          <Check className="h-4 w-4" />
        </span>
      )}
      {icon
        ? <img src={icon} alt="" className="h-10 w-10 rounded object-cover" />
        : (fallbackIcon || <Layers className="h-10 w-10 text-muted-foreground" />)}
      <span className="text-sm font-medium text-foreground line-clamp-2 leading-snug">{label}</span>
    </button>
  );
}

export function CorpusSelector({ industries, loading, selected, onSelect }) {
  const { t } = useTranslation();
  return (
    <SectionCard title={t('idea.corpusTitle')} testId="corpus-selector-card">
      <p className="text-sm text-muted-foreground -mt-2"><T k="idea.corpusHint" /></p>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="industry-tiles-loading">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="industry-tiles">
          <IndustryTile
            active={!selected}
            label={<T k="idea.industryNone" />}
            onClick={() => onSelect(null)}
            testId="industry-tile-none"
            fallbackIcon={<Layers className="h-10 w-10 text-muted-foreground" />}
          />
          {industries.map((ind) => (
            <IndustryTile
              key={ind.industry_key}
              active={selected === ind.industry_key}
              icon={ind.icon}
              label={ind.industry_label}
              onClick={() => onSelect(ind.industry_key)}
              testId={`industry-tile-${ind.industry_key}`}
            />
          ))}
        </div>
      )}
      {!loading && industries.length === 0 && (
        <p className="text-sm text-muted-foreground" data-testid="industry-tiles-empty"><T k="idea.tilesEmpty" /></p>
      )}
    </SectionCard>
  );
}

// The separate "choose a model" SectionCard that used to sit beside the corpus selector was REMOVED
// (Ali, 2026-08-10). It was a second picker driving the SAME `selectedModel` state as ModelCostBar,
// so the page asked the same question twice and only one of the two showed what it would cost.
// ModelCostBar is now the single picker and it moved to the top of the page — see the render below.

// The ONLY two user-editable fields of a generated idea (Ali, 2026-08-03): ideal customer and
// key features — the levers the market and build-pack prompts pull hardest on. The edit is a
// plain save, no model call, no charge; the server appends a customize-series version so
// history stays honest about what later analyses were grounded on.
function EditIdeaDialog({ mode, idea, projectId, onClose, onSaved }) {
  const { t } = useTranslation();
  const [icp, setIcp] = useState('');
  const [feats, setFeats] = useState([]);
  const [saving, setSaving] = useState(false);
  const open = mode !== null;
  useEffect(() => {
    if (!open) return;
    setIcp(typeof idea?.icp === 'string' ? idea.icp : '');
    setFeats(Array.isArray(idea?.key_features) ? idea.key_features.map(String) : []);
  }, [open, mode, idea]);

  const save = async () => {
    const fields = mode === 'icp'
      ? { icp: icp.trim() }
      : { key_features: feats.map((f) => f.trim()).filter(Boolean) };
    if ((mode === 'icp' && !fields.icp) || (mode === 'features' && fields.key_features.length === 0)) {
      toast.error(t('idea.editEmpty'));
      return;
    }
    setSaving(true);
    try {
      const r = await api.out('edit_idea_fields', { project_id: projectId, ...fields });
      await onSaved(r);
      onClose();
      toast.success(t('idea.editSaved'));
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent data-testid="idea-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">
            {t(mode === 'icp' ? 'idea.editIcpTitle' : 'idea.editFeaturesTitle')}
          </DialogTitle>
        </DialogHeader>
        {mode === 'icp' ? (
          <Textarea value={icp} onChange={(e) => setIcp(e.target.value)} rows={4} data-testid="edit-icp-input" />
        ) : (
          <div className="flex flex-col gap-2">
            {feats.map((f, i) => (
              // Index keys are safe here: rows are edited in place and removed by index.
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={f}
                  onChange={(e) => setFeats((p) => p.map((x, j) => (j === i ? e.target.value : x)))}
                  data-testid={`edit-feature-${i}`}
                />
                <button
                  type="button"
                  onClick={() => setFeats((p) => p.filter((_, j) => j !== i))}
                  className="text-muted-foreground hover:text-foreground"
                  data-testid={`edit-feature-remove-${i}`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            ))}
            <div>
              <Button variant="outline" size="sm" onClick={() => setFeats((p) => [...p, ''])}
                disabled={feats.length >= 20} data-testid="edit-feature-add">
                <Plus className="h-4 w-4 me-2" /> {t('idea.editAddFeature')}
              </Button>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="edit-cancel-button">
            {t('idea.editCancel')}
          </Button>
          <Button onClick={save} disabled={saving} data-testid="edit-save-button">
            {saving ? t('common.loading') : t('idea.editSave')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function IdeaResult({ idea, onEdit }) {
  const { t } = useTranslation();
  const citations = Array.isArray(idea.citations) ? idea.citations.filter(Boolean) : [];
  // `sources` (added by generate-idea) resolves an opaque "S1" label to the real document.
  // Older idea versions have no `sources` — those fall back to rendering the bare label.
  //
  // Models are inconsistent about citation format: some emit "S1", others "[S1]", some with
  // stray whitespace. Normalise on BOTH sides of the lookup so the match never depends on
  // which model produced the object.
  const citeKey = (v) => String(v ?? '').trim().replace(/^\[+/, '').replace(/\]+$/, '');
  const sourceById = new Map(
    (Array.isArray(idea.sources) ? idea.sources : []).map((s) => [citeKey(s.id), s]),
  );
  return (
    <div className="flex flex-col gap-3" data-testid="idea-output">
      {/* Hero card — product name + tagline + viability score */}
      <section
        className="border border-border bg-card rounded-xl shadow-sm p-6 md:p-8 flex flex-col sm:flex-row sm:items-center gap-6"
        data-testid="idea-output-card"
      >
        <ScoreGauge score={idea.score} testId="idea-score-gauge" />
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-foreground" data-testid="idea-name">{idea.name}</h2>
            <ProvenanceLabel value={idea.score_label} />
          </div>
          <p className="text-lg text-muted-foreground leading-relaxed mt-1.5" data-testid="idea-one-liner">{idea.one_liner}</p>
          <span className="sr-only" data-testid="idea-score">{idea.score}</span>
        </div>
      </section>

      {idea.no_corpus_note && <Disclaimer>{idea.no_corpus_note}</Disclaimer>}

      <InfoCard icon={AlertCircle} title={t('idea.fieldProblem')} testId="idea-card-problem">{idea.problem}</InfoCard>
      <InfoCard icon={Lightbulb} title={t('idea.fieldSolution')} testId="idea-card-solution">{idea.solution}</InfoCard>
      <InfoCard icon={ListChecks} title={t('idea.fieldKeyFeatures')} testId="idea-card-features"
        action={onEdit && (
          <Button variant="ghost" size="sm" onClick={() => onEdit('features')} data-testid="edit-features-button">
            <Pencil className="h-4 w-4 me-1" /> {t('idea.edit')}
          </Button>
        )}>
        <FeatureChips items={idea.key_features} testId="idea-key-features" />
      </InfoCard>
      <InfoCard icon={Users} title={t('idea.fieldIcp')} testId="idea-card-icp"
        action={onEdit && (
          <Button variant="ghost" size="sm" onClick={() => onEdit('icp')} data-testid="edit-icp-button">
            <Pencil className="h-4 w-4 me-1" /> {t('idea.edit')}
          </Button>
        )}>{idea.icp}</InfoCard>
      <InfoCard icon={Rocket} title={t('idea.fieldWhatFirst')} testId="idea-card-what-first">{idea.what_to_build_first || '—'}</InfoCard>
      <InfoCard icon={SearchX} title={t('idea.fieldMarketGap')} testId="idea-card-market-gap">{idea.market_gap || '—'}</InfoCard>
      <InfoCard icon={Fingerprint} title={t('idea.fieldDifferentiation')} testId="idea-card-differentiation">{idea.differentiation_angle}</InfoCard>
      <InfoCard icon={Shield} title={t('idea.fieldMoat')} testId="idea-card-moat">{idea.moat}</InfoCard>

      <div className="grid sm:grid-cols-2 gap-3">
        <InfoCard icon={ThumbsUp} title={t('idea.fieldStrengths')} testId="idea-card-strengths"><Bullets items={idea.strengths} testId="idea-strengths" /></InfoCard>
        <InfoCard icon={ThumbsDown} title={t('idea.fieldWeaknesses')} testId="idea-card-weaknesses"><Bullets items={idea.weaknesses} testId="idea-weaknesses" /></InfoCard>
        <InfoCard icon={CheckCircle2} title={t('idea.fieldGreenLights')} testId="idea-card-green"><Bullets items={idea.green_lights} testId="idea-green-lights" /></InfoCard>
        <InfoCard icon={AlertTriangle} title={t('idea.fieldRedFlags')} testId="idea-card-red"><Bullets items={idea.red_flags} testId="idea-red-flags" /></InfoCard>
      </div>

      {citations.length > 0 && (
        <InfoCard icon={Link2} title={t('idea.fieldSources')} testId="idea-card-sources">
          <ul className="space-y-3" data-testid="idea-sources">
            {citations.map((c, i) => {
              const s = sourceById.get(citeKey(c));
              // No provenance for this label (pre-`sources` version) — show it as-is rather
              // than inventing a title.
              if (!s) {
                return <li key={`${c}-${i}`} className="font-data text-sm text-muted-foreground">{c}</li>;
              }
              const label = s.title || s.url || s.id;
              return (
                <li key={`${c}-${i}`} className="text-sm" data-testid={`idea-source-${s.id}`}>
                  <span className="font-data text-xs text-muted-foreground me-1">[{s.id}]</span>
                  {s.url
                    ? (
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-medium text-primary hover:underline"
                      >
                        {label}
                      </a>
                    )
                    : <span className="font-medium text-foreground">{label}</span>}
                  {s.date && <span className="text-muted-foreground"> · {s.date}</span>}
                  {s.excerpt && (
                    <p className="mt-1 text-muted-foreground leading-relaxed whitespace-pre-line">{s.excerpt}</p>
                  )}
                </li>
              );
            })}
          </ul>
        </InfoCard>
      )}
    </div>
  );
}

export default function IdeaStage({ projectId, language, selectedIndustryKey, selectedIndustryLang, origin, onChanged, servedModels }) {
  const { t } = useTranslation();
  const [idea, setIdea] = useState(null);
  const [seed, setSeed] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [streamingIdea, setStreamingIdea] = useState(null);
  // "Surprise me" teaser sparks (AI door): fetched on demand, click one to seed the generation.
  const [seeds, setSeeds] = useState(null);
  const [seedsLoading, setSeedsLoading] = useState(false);
  const [seedsFailed, setSeedsFailed] = useState(false); // one failure disarms the auto-fetch
  const [creditBlocked, setCreditBlocked] = useState(false);
  const [lastRun, setLastRun] = useState(null); // [regenerate, modelOverride] for the banner's resume
  const [pickedTitle, setPickedTitle] = useState('');
  const [seedCredits, setSeedCredits] = useState(null); // credits the spark call actually charged

  const corpusLang = selectedIndustryLang || language || 'en'; // A2: account-based, not toggleable
  const [industries, setIndustries] = useState([]);
  const [tilesLoading, setTilesLoading] = useState(false);
  const [selected, setSelected] = useState(selectedIndustryKey || null);
  // N-35: useState reads its argument on the FIRST render only. The project row loads
  // asynchronously, so when `selectedIndustryKey` arrives after mount — which is the normal case
  // coming from the door dialog — `selected` stayed null and the stage silently reverted to
  // General. The user picked an industry at the door and was then quietly given no corpus.
  // It is a race, which is why it did not reproduce every time: whether the prop beat the mount
  // decided it. Syncing the prop removes the race in both directions.
  useEffect(() => {
    if (selectedIndustryKey) setSelected(selectedIndustryKey);
  }, [selectedIndustryKey]);
  const vh = useVersions(projectId, 'idea_object');
  const [editMode, setEditMode] = useState(null); // null | 'icp' | 'features'

  // Wallet meter (credits): model picker + dry-run round cost. Shown only when metering is ON.
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [cost, setCost] = useState(null); // { credits, credit_usd_peg, metering_enabled }
  const [available, setAvailable] = useState(null);

  useEffect(() => {
    api.listSelectableModels().then((r) => {
      const ms = r.models || [];
      setModels(ms);
      // Do NOT auto-pick a model: an empty selection means "let the server resolve the default".
      // A model_id is sent ONLY when the user explicitly picks one in ModelCostBar (an override).
    }).catch(() => {});
    api.getWallet().then((w) => setAvailable(w?.wallet?.available ?? null)).catch(() => {});
  }, []);

  useEffect(() => {
    // industry_key + lang let the server apply the same free/paywalled gate the charge uses, so
    // the quoted number matches what the ledger records (a `free` corpus quotes no RAG charge).
    api.walletPreview({
      model_id: selectedModel || undefined,
      corpus_used: !!selected,
      industry_key: selected || undefined,
      lang: corpusLang,
    })
      .then(setCost).catch(() => setCost(null));
  }, [selectedModel, selected, corpusLang]);

  const meteringOn = cost?.metering_enabled === true;
  const shortfall = meteringOn && available != null && cost?.credits != null && cost.credits > available;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.out('get_idea', { project_id: projectId });
      setIdea(r.idea_object);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  const loadTiles = useCallback(async (lang) => {
    setTilesLoading(true);
    try {
      const r = await api.listIndustries(lang);
      setIndustries(r.industries || []);
    } catch (e) {
      setIndustries([]);
    } finally {
      setTilesLoading(false);
    }
  }, []);
  useEffect(() => { loadTiles(corpusLang); }, [corpusLang, loadTiles]);

  const isAi = origin === 'ai';

  const loadSeeds = useCallback(async () => {
    setSeedsLoading(true);
    setSeedsFailed(false);
    try {
      const r = await api.suggestSeeds({ project_id: projectId, industry_key: selected, lang: corpusLang });
      setSeeds(r.seeds || []);
      setCreditBlocked(false);
      // The sparks are now a CHARGED model call, and on the AI door they run without the user
      // pressing anything. Say what it cost rather than letting the balance move unexplained.
      if (r.credits_charged != null) {
        setSeedCredits(r.credits_charged);
        api.getWallet().then((w) => setAvailable(w?.wallet?.available ?? null)).catch(() => {});
      }
    } catch (e) {
      setSeedsFailed(true);
      if (isCreditError(e)) setCreditBlocked(true);
      else toast.error(e.message);
    } finally {
      setSeedsLoading(false);
    }
  }, [projectId, selected, corpusLang]);

  // N-50: fetch the sparks on landing instead of waiting for a "Surprise me" click.
  //
  // WHY THIS IS THE FIX. The AI door promises "a starter idea grounded in your selected industry",
  // but with no spark picked there is no seed text — and therefore no query specific enough to
  // retrieve anything, so the idea came back ungrounded every time (N-48/N-50). The spark IS the
  // query. Producing it automatically means the grounded path is the default rather than something
  // the user has to know to ask for.
  //
  // Guarded to fire ONCE: `seeds === null` means "never fetched" (an empty array means "fetched,
  // model returned none"), so a re-render cannot re-charge the user for another spark round.
  useEffect(() => {
    if (!isAi || loading || idea || seeds !== null || seedsLoading) return;
    if (seedsFailed || creditBlocked) return; // N-74: never auto-retry a failed/unpayable fetch
    loadSeeds();
  }, [isAi, loading, idea, seeds, seedsLoading, seedsFailed, creditBlocked, loadSeeds]);

  const pickSeed = (s) => {
    setPickedTitle(s.title);
    setSeed(`${s.title} — ${s.one_liner}`);
  };

  const run = async (regenerate, modelOverride) => {
    setCreditBlocked(false);
    if (!isAi && !regenerate && seed.trim().length < 8) {
      toast.error(t('idea.seedRequired'));
      return;
    }
    setBusy(true);
    setStreamingIdea(null);
    try {
      const r = await api.streamIdea(
        {
          project_id: projectId,
          seed_text: regenerate ? (seed || idea?.one_liner || 'refine') : seed,
          regenerate,
          industry_key: selected,
          lang: corpusLang,
          origin: isAi ? 'ai' : undefined,
          model_id: (modelOverride ?? selectedModel) || undefined,
          // #7 binding half. Send the version actually ON SCREEN so Regenerate refines
          // THAT one, and the server records it as the parent. Without this the server
          // falls back to the live row - which is "latest" regardless of what you are
          // looking at, the exact behaviour migration 044 could not fix on its own.
          // Only on regenerate: a fresh generate has no parent to bind to.
          ...(regenerate && vh.selected != null ? { from_version_no: vh.selected } : {}),
        },
        {
          onDelta: (partial) => {
            const obj = parsePartialJson(partial);
            if (obj && typeof obj === 'object') setStreamingIdea(obj);
          },
        },
      );
      setStreamingIdea(null);
      setIdea(r.idea_object);
      onChanged?.();
      await vh.refresh();
      toast.success(regenerate ? t('idea.regenerated', { version: r.version_no }) : t('idea.created'));
    } catch (e) {
      if (isCreditError(e)) { setCreditBlocked(true); setLastRun([regenerate, modelOverride]); }
      else toast.error(e.message);
    } finally {
      setStreamingIdea(null);
      setBusy(false);
    }
  };

  const primaryLabel = () => {
    if (busy) return t('idea.generating');
    if (isAi) return idea ? t('idea.generateFresh') : t('idea.aiGenerate');
    return idea ? t('idea.generateFresh') : t('idea.generate');
  };

  const shownIdea = streamingIdea || vh.viewPayload || idea;
  let body;
  if (loading) {
    body = <SectionCard testId="idea-loading"><Skeleton className="h-40 w-full" /></SectionCard>;
  } else if (shownIdea) {
    body = (
      <div className="flex flex-col gap-3">
        {/* Edit is offered only on the LIVE latest idea — editing while viewing an older
            version would silently save that older content over the current one. */}
        <IdeaResult idea={shownIdea} onEdit={!streamingIdea && !vh.viewPayload && idea ? setEditMode : null} />
        {!streamingIdea && (
          <div className="flex flex-col gap-2">
            <ServedModelBadge model={vh.selectedModel || servedModels?.['generate-idea']} testId="idea-served-model" />
          </div>
        )}
      </div>
    );
  } else {
    body = <div className="text-muted-foreground text-sm px-2" data-testid="idea-empty">{t(isAi ? 'idea.aiEmpty' : 'idea.empty')}</div>;
  }

  return (
    <div className="flex flex-col gap-6">
      {/* The corpus selector exists to frame the GENERATION of an idea. Once one exists the
          industry is already baked into it, so the box goes the same way the input card does —
          on both doors (by me / by AI). Gated on !loading too: while the idea is still being
          fetched, "no idea yet" is unknown, not true — rendering the box then made it flash
          for a beat on every existing idea's page. */}
      {!loading && !idea && (
      <div className="flex flex-col gap-6">
        {/* Model picker + round cost, ABOVE the corpus selector (Ali, 2026-08-10): the model and its
            price are the first decision, and the corpus choice moves that price (+RAG on a paywalled
            corpus), so the number has to be on screen before the tile is picked — not below the
            generate button where it was read only after deciding.
            NOT gated on metering: this is now the only model picker on the page, so it must render
            even when metering is OFF. The credit figures are what depend on metering, and
            ModelCostBar already omits them when they are null. */}
        <ModelCostBar
          models={models}
          selectedModel={selectedModel}
          onSelectModel={setSelectedModel}
          credits={meteringOn ? (cost?.credits ?? null) : null}
          available={meteringOn ? available : null}
        />
        <CorpusSelector
          industries={industries}
          loading={tilesLoading}
          selected={selected}
          onSelect={setSelected}
        />
      </div>
      )}
      {/* The input card exists to CREATE an idea. Once one exists it is changed through the
          changes editor below, so the whole card goes - hiding only its contents left a
          titled box with nothing in it. */}
      {!idea && (
      <SectionCard title={t(isAi ? 'idea.aiGenerate' : 'idea.inputTitle')} testId="idea-input-card">
        {isAi ? (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-muted-foreground" data-testid="idea-ai-help"><T k="idea.aiHelp" /></p>
            <div>
              <Button variant="outline" size="sm" onClick={() => { setCreditBlocked(false); loadSeeds(); }} disabled={seedsLoading || busy} data-testid="surprise-me-button">
                <Wand2 className="h-4 w-4 me-2" />
                {seedsLoading ? t('idea.surpriseLoading') : (seeds ? t('idea.surpriseAgain') : t('idea.surpriseMe'))}
              </Button>
            </div>
            {seedsLoading && (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="seeds-loading">
                {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}
              </div>
            )}
            {!seedsLoading && Array.isArray(seeds) && seeds.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="seed-cards">
                {seeds.map((s, i) => (
                  <button
                    key={`${s.title}-${i}`}
                    type="button"
                    onClick={() => pickSeed(s)}
                    aria-pressed={pickedTitle === s.title}
                    data-testid={`seed-card-${i}`}
                    className={`text-start rounded-md border p-4 transition-colors ${
                      pickedTitle === s.title
                        ? 'border-primary ring-2 ring-primary bg-muted'
                        : 'border-border hover:border-muted-foreground bg-card'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-heading font-semibold text-foreground">{s.title}</span>
                      {pickedTitle === s.title && <Check className="h-4 w-4 text-foreground shrink-0" />}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 leading-snug">{s.one_liner}</p>
                  </button>
                ))}
              </div>
            )}
            {/* What the automatic spark call cost. Shown because the user did not press anything
                to trigger it — an unexplained balance drop is exactly what the honesty labelling
                exists to prevent. */}
            {seedCredits != null && (
              <p className="text-xs text-muted-foreground" data-testid="seeds-charged">
                {t('idea.seedsCharged', { credits: seedCredits })}
              </p>
            )}
            {pickedTitle
              ? <p className="text-xs text-muted-foreground" data-testid="seed-picked-hint"><T k="idea.seedPickedHint" /></p>
              : (Array.isArray(seeds) && seeds.length > 0 && (
                <p className="text-xs text-muted-foreground" data-testid="seed-pick-required"><T k="idea.seedPickRequired" /></p>
              ))}
          </div>
        ) : (
          // #6 - the free-text seed box is RETIRED once an idea exists.
          // It used to stay live forever, so the primary button kept generating another
          // from-scratch idea and the version list filled with junk nobody meant to create.
          // Refining an idea is Regenerate; REPLACING it is now a deliberate second step.
          !idea && (
            <Textarea
              value={seed}
              onChange={(e) => setSeed(e.target.value)}
              placeholder={t('idea.seedPlaceholder')}
              className="min-h-[120px] resize-y p-4 text-base"
              data-testid="idea-seed-input"
            />
          )
        )}
        {creditBlocked && (
          <CreditBanner testId="idea-credit-banner" onResume={() => run(...(lastRun || [false, undefined]))} />
        )}
        <div className="flex gap-3 flex-wrap">
          {/* N-50: on the AI door the button waits for a spark. Without one there is no seed,
              so the corpus cannot be queried and the "grounded in your selected industry" promise
              silently fails — which is the whole defect. The sparks arrive on their own (see the
              effect above), so this asks the user to choose, never to go and fetch. */}
          {!idea && (
            <Button
              onClick={() => run(false)}
              disabled={generateBlocked({ busy, shortfall, isAi, pickedTitle })}
              title={isAi && !pickedTitle ? t('idea.seedPickRequired') : undefined}
              data-testid="generate-idea-button"
            >
              <Sparkles className="h-4 w-4 me-2" />
              {primaryLabel()}
            </Button>
          )}
        </div>
      </SectionCard>
      )}
      <GenerationProgress
        running={busy && !streamingIdea}
        messages={t('progress.idea', { returnObjects: true })}
        durationMs={15000}
        testId="idea-generation-progress"
      />
      {body}
      <EditIdeaDialog
        mode={editMode}
        idea={idea}
        projectId={projectId}
        onClose={() => setEditMode(null)}
        onSaved={async (r) => {
          setIdea(r.idea_object);
          onChanged?.();
          await vh.refresh();
        }}
      />
    </div>
  );
}
